import React, { useState } from 'react';
import { Calendar, User, Clock, ArrowLeft, Share2, Twitter, Linkedin, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/* ─── FAQ Accordion ─────────────────────────────────────── */
const faqs = [
  {
    q: "What did Prime Minister Modi say about fuel conservation?",
    a: "Prime Minister Narendra Modi publicly appealed to all Indians to voluntarily reduce fuel use and reduced his own convoy as a visible first step. He called for a national shift toward cleaner, more energy-efficient transport, with electric vehicles at the centre of that transition.",
  },
  {
    q: "How do electric vehicles help reduce fuel consumption in India?",
    a: "EVs run entirely on electricity, eliminating petrol use for every ride taken. Across millions of daily commutes, this directly reduces India's oil import demand and lowers urban air pollution, contributing to the exact fuel conservation goal Prime Minister Modi called for.",
  },
  {
    q: "Is India's EV adoption growing in 2026?",
    a: "Strongly yes. India recorded 24.5 lakh EV sales in FY2026, a 25% year-on-year increase, with March 2026 hitting a record 2,80,093 units. The growth is driven by an ecosystem that is finally complete, government policy, GST cut to 5%, over 29,000 charging stations, falling EV prices, and ride-hailing apps giving commuters access without ownership. When policy, infrastructure, affordability, and accessibility align, adoption does not just grow; it accelerates.",
  },
  {
    q: "How can commuters switch to electric vehicle rides today?",
    a: "It is simpler than most people think. You do not need to own an electric vehicle to be part of the change. Ride-hailing apps have made electric mobility available to every commuter, anytime, anywhere, with no barriers in the way. The shift can begin today, with nothing more than a single ride.",
  },
  {
    q: "What does this mean for Chennai commuters in 2026?",
    a: "For Chennai commuters, NURA Electric Mobility is that switch. Book an electric auto ride in minutes, get instantly matched with a nearby driver, enjoy fixed pricing with no bargaining, and rely on rides that show up without last-minute cancellations.",
  },
];

function FAQItem({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-white/5 transition-colors"
        aria-expanded={open}
      >
        <span className="text-white font-semibold pr-4">{faq.q}</span>
        {open
          ? <ChevronUp size={18} className="text-[#bceb39] flex-shrink-0" />
          : <ChevronDown size={18} className="text-[#bceb39] flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-6 pb-5 text-gray-300 text-base leading-relaxed border-t border-white/10 pt-4">
          {faq.a}
        </div>
      )}
    </div>
  );
}

/* ─── Main Section ───────────────────────────────────────── */
const ModiFuelSection = () => {
  const navigate = useNavigate();

  return (
    <article
      className="min-h-screen bg-black text-white"
      itemScope
      itemType="https://schema.org/Article"
    >
      {/* ── Hero Header ── */}
      <div className="max-w-4xl mx-auto px-6 pt-16 pb-10">
        <span className="inline-block bg-[#bceb39] text-black text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">
          Electric Mobility
        </span>
        <h1
          className="text-4xl md:text-5xl font-bold leading-tight text-white"
          itemProp="headline"
        >
          Modi's Fuel Conservation Appeal and India's Electric Vehicle Moment
        </h1>
      </div>

      {/* ── Meta Bar ── */}
      <div className="max-w-4xl mx-auto px-6 py-5 border-y border-gray-800 flex flex-wrap gap-6 items-center justify-between mb-4">
        <div className="flex flex-wrap gap-5 text-sm text-gray-400">
          <span className="flex items-center gap-2" itemProp="author" itemScope itemType="https://schema.org/Person">
            <User size={15} className="text-[#bceb39]" />
            <span itemProp="name">Nura Team</span>
          </span>
          <span className="flex items-center gap-2">
            <Calendar size={15} className="text-[#bceb39]" />
            <time itemProp="datePublished" dateTime="2026-05-01">May 1, 2026</time>
          </span>
          <span className="flex items-center gap-2">
            <Clock size={15} className="text-[#bceb39]" />
            5 min read
          </span>
        </div>
        <div className="flex items-center gap-3 text-gray-400 text-sm">
          <Share2 size={15} className="text-[#bceb39]" />
          <span>Share:</span>
          <a
            href="https://twitter.com/intent/tweet?text=Modi%27s+Fuel+Conservation+Appeal+and+India%27s+EV+Moment&url=https://nuraemobility.co.in/blog/pm-modi-fuel-conservation-india-electric-vehicles"
            target="_blank" rel="noopener noreferrer"
            aria-label="Share on Twitter"
            className="hover:text-[#bceb39] transition-colors"
          >
            <Twitter size={17} />
          </a>
          <a
            href="https://www.linkedin.com/sharing/share-offsite/?url=https://nuraemobility.co.in/blog/pm-modi-fuel-conservation-india-electric-vehicles"
            target="_blank" rel="noopener noreferrer"
            aria-label="Share on LinkedIn"
            className="hover:text-[#bceb39] transition-colors"
          >
            <Linkedin size={17} />
          </a>
        </div>
      </div>

      {/* ── Article Body ── */}
      <div
        className="max-w-4xl mx-auto px-6 py-10 space-y-12 text-gray-300 leading-relaxed text-lg"
        itemProp="articleBody"
      >

        {/* ── INTRO ── */}
        <section>
          <p>
            Prime Minister Narendra Modi reduced his convoy size, hiked the gold import duty to cut
            oil dependence, and publicly urged every Indian to save fuel. More than a policy move,
            it was a national mirror held up to every commuter, every driver, and every city.
          </p>
        </section>

        {/* ── SECTION 1 ── */}
        <section aria-labelledby="section-modi">
          <h2 id="section-modi" className="text-2xl md:text-3xl font-bold text-white mb-6">
            What Modi Said About Fuel and Why Every Commuter Should Pay Attention
          </h2>
          <p className="mb-4">
            India imports over 85% of its crude oil, a dependency that costs over{' '}
            <span className="text-[#bceb39] font-semibold">&#8377;12 lakh crore</span> every single
            year. Prime Minister Narendra Modi's appeal to save fuel is backed by numbers that
            prove the shift is already underway.
          </p>
          <p>
            India's push to cut fuel dependency through electric mobility is already producing
            results. In FY2026, the country recorded{' '}
            <span className="text-[#bceb39] font-semibold">24.5 lakh EV sales</span>, a 25%
            year-on-year jump, with March 2026 alone hitting a record of 2,80,093 units in a
            single month. Electric two-wheelers and three-wheelers together account for over 91%
            of all EV sales, making them the true engine of India's clean commute revolution.
          </p>
        </section>

        {/* ── SECTION 2 ── */}
        <section aria-labelledby="section-momentum">
          <h2 id="section-momentum" className="text-2xl md:text-3xl font-bold text-white mb-6">
            India's EV Momentum in 2026: The Shift Is Already Happening
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 my-8">
            {[
              { stat: '24.5L', label: 'EV sales in FY2026, up 25% YoY' },
              { stat: '5%', label: 'GST on EVs vs 28% on petrol vehicles' },
              { stat: '29,000+', label: 'charging stations live across India' },
            ].map((item) => (
              <div key={item.stat} className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center">
                <p className="text-3xl font-bold text-[#bceb39] mb-1">{item.stat}</p>
                <p className="text-gray-400 text-sm">{item.label}</p>
              </div>
            ))}
          </div>

          <p>
            In 2026, India's EV market is breaking records while building infrastructure. Monthly
            sales crossed 2 lakh units five times in FY2026. GST on electric vehicles stands at
            just 5% against 28% on petrol vehicles, and over 29,000 charging stations are live
            across the country, proving that India's 30% EV target by 2030 was never just a
            promise. It was always a plan.
          </p>
        </section>

        {/* ── SECTION 3 ── */}
        <section aria-labelledby="section-commute">
          <h2 id="section-commute" className="text-2xl md:text-3xl font-bold text-white mb-6">
            Your Commute Is Your Vote on Fuel Conservation
          </h2>
          <p>
            What India actually needs isn't fewer commuters, it's more electric rides. Commutes
            continue, routines stay intact, and every ride becomes a personal contribution to the
            very cause PM Narendra Modi stood up for. And India moves closer to fuel independence,
            not because people stayed home, but because they chose to ride smarter.
          </p>
        </section>

        {/* ── SECTION 4 ── */}
        <section aria-labelledby="section-conclusion">
          <h2 id="section-conclusion" className="text-2xl md:text-3xl font-bold text-white mb-6">
            Conclusion: The Call Has Been Made. Will You Answer It?
          </h2>
          <p className="mb-4">
            India's growing focus on fuel conservation is accelerating the shift toward electric
            mobility. With EV adoption increasing rapidly and cleaner transport becoming more
            accessible, everyday commuting choices are starting to play a bigger role in reducing
            fuel dependency. The transition has already begun, and it is only gaining momentum.
          </p>
          <p className="font-semibold text-white">
            The question is simple: will your next ride be part of the solution?
          </p>
        </section>

        {/* ── INTERNAL LINKS ── */}
        <section aria-labelledby="section-explore" className="bg-white/5 border border-white/10 rounded-2xl p-8">
          <h2 id="section-explore" className="text-xl font-bold text-white mb-5">
            Explore More From Nura
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: 'Our Electric Fleet', desc: "See how Nura's electric autos work", href: '/services' },
              { label: 'Drive With Us', desc: 'Become a Nura pilot in Chennai', href: '/drive-with-us' },
              { label: 'Our Safety Promise', desc: 'How we keep every ride safe', href: '/safety' },
            ].map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="block bg-black border border-white/10 rounded-xl px-5 py-4 hover:border-[#bceb39] transition-colors group"
              >
                <p className="text-white font-semibold group-hover:text-[#bceb39] transition-colors">
                  {link.label} &rarr;
                </p>
                <p className="text-gray-400 text-sm mt-1">{link.desc}</p>
              </a>
            ))}
          </div>
        </section>

        {/* ── FAQ ── */}
        <section aria-labelledby="section-faq">
          <h2 id="section-faq" className="text-2xl md:text-3xl font-bold text-white mb-2">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-400 mb-8 text-base">
            Common questions about PM Modi's fuel conservation appeal and India's EV shift.
          </p>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <FAQItem key={i} faq={faq} />
            ))}
          </div>
        </section>

        {/* ── TAGS ── */}
        <div className="pt-2 flex flex-wrap gap-2">
          {[
            'FuelConservation', 'PMModiEV', 'ElectricVehicle', 'IndiaEV',
            'ElectricAuto', 'Chennai', 'Sustainability', 'EVAdoption2026',
          ].map((tag) => (
            <span
              key={tag}
              className="bg-white/5 border border-white/10 text-gray-400 text-xs px-3 py-1.5 rounded-full hover:border-[#bceb39] hover:text-[#bceb39] transition-colors cursor-default"
            >
              #{tag}
            </span>
          ))}
        </div>

        {/* ── CTA ── */}
        <div className="bg-[#bceb39] rounded-2xl p-8 md:p-12 text-center">
          <p className="text-black text-xs font-bold uppercase tracking-widest mb-3">
            Ride Clean. Start Today with Nura.
          </p>
          <h2 className="text-black text-2xl md:text-3xl font-bold mb-3 leading-tight">
            Your commute can be part of the solution.
          </h2>
          <p className="text-black/70 mb-6 text-base max-w-xl mx-auto">
            Book your first electric auto ride in Chennai. No petrol. No guilt. Just a smarter commute.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://app.nuraemobility.co.in/?c=YImSUF"
              target="_blank" rel="noopener noreferrer"
              className="inline-block bg-black text-[#bceb39] font-bold px-8 py-3 rounded-full hover:scale-105 transition-transform"
            >
              Download on Google Play &rarr;
            </a>
            <a
              href="https://app.nuraemobility.co.in/?c=YImSUF"
              target="_blank" rel="noopener noreferrer"
              className="inline-block bg-black text-[#bceb39] font-bold px-8 py-3 rounded-full hover:scale-105 transition-transform"
            >
              Download on App Store &rarr;
            </a>
          </div>
        </div>

      </div>

      {/* ── Back Button ── */}
      <div className="max-w-4xl mx-auto px-6 pb-16">
        <button
          onClick={() => navigate('/blog')}
          className="flex items-center gap-2 text-gray-400 hover:text-[#bceb39] transition-colors text-sm font-medium"
        >
          <ArrowLeft size={16} />
          Back to Blog
        </button>
      </div>

    </article>
  );
};

export default ModiFuelSection;
